import './style.scss';

import React, { useCallback, useMemo, useState } from 'react';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { Divider, Icon } from 'semantic-ui-react';
import { MultiCascader, MultiCascaderProps } from 'rsuite';
import { ItemDataType } from 'rsuite/esm/MultiCascadeTree';

type CascaderData = ItemDataType<string>[];

type Props<T> = MultiCascaderProps & {
  data: CascaderData;
  value: string[];
  onChange: React.ComponentProps<typeof MultiCascader>['onChange'];
  valuesByKey: Record<string, T>;
  invalidValueIntlKey: string;
  invalidChildValueIntlKey: string;
  isChildKey?: (valueKey: string, key: string) => boolean;
  validityByKey?: Record<string, boolean>;
};

// Simple stable hash -> hue
const hueFromObject = (obj: unknown): number => {
  const str = JSON.stringify(obj);
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return Math.abs(h) % 360;
};

const colorFromObject = (obj: unknown, s = 80, l = 80) =>
  `hsl(${hueFromObject(obj)}, ${s}%, ${l}%)`;

type FooterDisplayProps = {
  keyLabel: string | React.ReactNode;
  associatedName: string;
};

function AssociatedFooterDisplay({ keyLabel, associatedName }: FooterDisplayProps) {
  return (
    <div className="color-coded-multi-cascader--footer-display">
      <span className="color-coded-multi-cascader--footer-display__key">{keyLabel}</span>
      <Icon name="long arrow alternate right" />
      <span className="color-coded-multi-cascader--footer-display__value">{associatedName}</span>
    </div>
  );
}

export default function ColorCodedMultiCascader<T>({
  data,
  value,
  onChange,
  valuesByKey,
  isChildKey,
  invalidValueIntlKey,
  invalidChildValueIntlKey,
  validityByKey = {},
  popupClassName,
  ...cascaderProps
}: Props<T>) {
  const intl = useIntl();

  const [hoveredAssociatedName, setHoveredAssociatedName] = useState<
    FooterDisplayProps | undefined
  >(undefined);
  const [selectedAssociatedName, setSelectedAssociatedName] = useState<
    FooterDisplayProps | undefined
  >(undefined);

  const colorIndicators = useMemo(() => {
    const result: Record<string, string> = {};
    for (const [key, constants] of Object.entries(valuesByKey)) {
      if (!constants) continue;
      const existing = Object.keys(result).find((k) => _.isEqual(valuesByKey[k], constants));
      if (existing) {
        result[key] = result[existing];
      } else {
        result[key] = colorFromObject(constants, 80, 80);
      }
    }
    return result;
  }, [valuesByKey]);

  const displayAssociatedNameFooter = useCallback(() => {
    if (hoveredAssociatedName) {
      return <AssociatedFooterDisplay {...hoveredAssociatedName} />;
    }
    if (selectedAssociatedName) {
      return <AssociatedFooterDisplay {...selectedAssociatedName} />;
    }
    return null;
  }, [selectedAssociatedName, hoveredAssociatedName]);

  return (
    <MultiCascader
      {...cascaderProps}
      popupClassName={`color-coded-multi-cascader--popup ${popupClassName}`}
      searchable={false}
      data={data}
      value={value}
      onChange={onChange}
      onSelect={(item, selectedPaths) => {
        for (const { value } of selectedPaths.toReversed()) {
          const associatedName = (valuesByKey[value as string] as { name?: string })?.name;
          if (associatedName) {
            setSelectedAssociatedName({ keyLabel: item.label, associatedName });
            return;
          }
        }
      }}
      renderExtraFooter={displayAssociatedNameFooter}
      renderTreeNode={(node, item) => {
        const itemValue = item?.value as string;

        const isInvalid = validityByKey[itemValue] === false;
        let isChildInvalid = false;
        if (!isInvalid) {
          isChildInvalid = _.some(
            Object.keys(valuesByKey).filter((key) => isChildKey?.(itemValue, key) ?? false),
            (key) => validityByKey[key] === false
          );
        }

        const subColors = Object.keys(colorIndicators)
          .filter((key) => isChildKey?.(itemValue, key) ?? false)
          .sort((a, b) => {
            if (colorIndicators[a] === colorIndicators[b]) return 0;
            return colorIndicators[a] < colorIndicators[b] ? -1 : 1;
          })
          .map((key) => <div style={{ backgroundColor: colorIndicators[key] }} key={key} />);

        const parentTree = [itemValue];
        let currentNode = item;
        let associatedName = (valuesByKey[itemValue] as { name?: string })?.name || undefined;
        while (currentNode.parent) {
          parentTree.push(currentNode.parent.value as string);
          currentNode = currentNode.parent;
          if (!associatedName) {
            associatedName =
              (valuesByKey[currentNode.value as string] as { name?: string })?.name || undefined;
          }
        }

        for (const region of parentTree) {
          const color = colorIndicators[region];
          if (color) {
            return (
              <div
                onMouseEnter={() =>
                  associatedName &&
                  setHoveredAssociatedName({ keyLabel: item.label, associatedName })
                }
                onMouseLeave={() => setHoveredAssociatedName(undefined)}
              >
                {isInvalid && (
                  <Icon
                    name="warning sign"
                    color="red"
                    title={intl.formatMessage({ id: invalidValueIntlKey })}
                  />
                )}
                {isChildInvalid && (
                  <Icon
                    title={intl.formatMessage({ id: invalidChildValueIntlKey })}
                    name="warning sign"
                    color="yellow"
                    className="error-indicator"
                  />
                )}
                {node}
                <div className="node-color-container" style={{ backgroundColor: color }}>
                  <div />
                  {subColors}
                </div>
              </div>
            );
          }
        }
        return node;
      }}
      locale={intl.locale as any}
    />
  );
}
